import { FLOW_ACTION_CUSTOM_CLAIM } from 'types/flows';
import { getCountryDisplayName } from '@utils/geo';
import lookup from 'country-code-lookup';
import { AuthMethods, OAuthProviders } from 'types/auth';

type Condition = {
	name: string;
	type: 'string' | 'number' | 'boolean';
	options?: IOption[];
	tags?: string[];
	description?: string;
};

const lookupOptions = lookup.countries
	.map((c) => ({
		label: c.iso2,
		value: getCountryDisplayName(c)
	}))
	.concat([
		{
			label: 'Embargoed Countries (US EAR)',
			value: 'Embargoed Countries (US EAR)'
		},
		{
			label: 'Sanctioned Countries (US EAR)',
			value: 'Sanctioned Countries (US EAR)'
		},
		{
			label: 'European Union',
			value: 'European Union'
		}
	]);

export interface IOption {
	label: string;
	value?: string;
	display?: string;
}

// this should come from the server, for now we holds it in the client
export const authInfoConditionKeys: Condition[] = [
	{
		name: 'authInfo.sessionJwt',
		type: 'string'
	},
	{
		name: 'authInfo.refreshJwt',
		type: 'string'
	},
	{
		name: 'authInfo.cookieDomain',
		type: 'string'
	},
	{
		name: 'authInfo.cookiePath',
		type: 'string'
	},
	{
		name: 'authInfo.cookieMaxAge',
		type: 'number'
	},
	{
		name: 'authInfo.cookieExpiration',
		type: 'number'
	},
	{
		name: 'authInfo.firstSeen',
		type: 'boolean',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'authInfo.user.userId',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'authInfo.user.loginIds',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'authInfo.user.name',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'authInfo.user.email',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'authInfo.user.emailDomain',
		type: 'string'
	},
	{
		name: 'authInfo.user.phone',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'authInfo.user.verifiedEmail',
		type: 'boolean',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'authInfo.user.verifiedPhone',
		type: 'boolean',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'authInfo.user.userTenants',
		type: 'string'
	},
	{
		name: 'authInfo.user.picture',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'deviceInfo.webAuthnSupport',
		type: 'boolean',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'startOptions.redirectURL',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	}
];
export const authStateConditionKeys: Condition[] = [
	{
		name: 'lastAuth.loginId',
		type: 'string'
	},
	{
		name: 'lastAuth.authMethod',
		type: 'string',
		options: AuthMethods.map((v) => ({
			display: `condition.dialog.authMethods.${v}`,
			label: v
		}))
	},
	{
		name: 'authState',
		type: 'string',
		options: [{ label: 'logged-in' }]
	}
];
export const coreConditionKeys: Condition[] = [
	{
		name: 'mailProviderHost',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'redirectURL',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'email',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'emailDomain',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'phone',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'provider',
		type: 'string',
		options: Object.values(OAuthProviders).map((v) => ({ label: v })),
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'tenant',
		type: 'string'
	},
	{
		name: 'externalId',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'fullName',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'firstName',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'lastName',
		type: 'string',
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'geoLocation',
		type: 'string',
		options: lookupOptions,
		tags: [FLOW_ACTION_CUSTOM_CLAIM]
	},
	{
		name: 'idpInitiated',
		type: 'boolean'
	},
	{
		name: 'ssoEnabled',
		type: 'boolean'
	}
];
export const riskConditionKeys: Condition[] = [
	{
		name: 'riskInfo.riskScore',
		type: 'number'
	},
	{
		name: 'riskInfo.botDetected',
		type: 'boolean'
	}
];
export const conditionKeys: Condition[] = [
	...authInfoConditionKeys,
	...authStateConditionKeys,
	...coreConditionKeys,
	...riskConditionKeys
];
// 'authInfo.user.ExternalIDs': ['string'], is missing from this list

export const operatorsOrder = [
	'contains',
	'empty',
	'not-empty',
	'equal',
	'not-equal',
	'greater-than',
	'less-than',
	'is-false',
	'is-true',
	'in',
	'not-in'
];

export const OperatorTypes = {
	targetless: ['empty', 'is-false', 'is-true', 'not-empty'],
	multiple: ['in', 'not-in']
};
